import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, MapPin, X, ChevronRight } from 'lucide-react';
import { Switch } from './ui/switch';
import { useApp } from '../context/AppContext';
import { formatMoney } from '../lib/money';
import api from '../services/api';

const PREF_KEY = 'shipitnow_mkt_home';

/**
 * HomeMarketplaceStrip — after the customer sets a delivery address we ask
 * (once) whether they want nearby marketplace items on the home page. Their
 * choice persists; a toggle lets them flip it any time.
 */
export const HomeMarketplaceStrip = () => {
  const { delivery } = useApp();
  const [pref, setPref] = useState(() => localStorage.getItem(PREF_KEY)); // 'on' | 'off' | null
  const [items, setItems] = useState([]);
  const hasAddress = delivery?.lat != null && delivery?.lng != null;

  const savePref = (v) => { localStorage.setItem(PREF_KEY, v); setPref(v); };

  useEffect(() => {
    if (pref !== 'on' || !hasAddress) { setItems([]); return; }
    let alive = true;
    api.get('/marketplace/listings', { params: { lat: delivery.lat, lng: delivery.lng, limit: 10 } })
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [pref, hasAddress, delivery?.lat, delivery?.lng]);

  if (!hasAddress) return null;

  // First time: ask the user.
  if (pref == null) {
    return (
      <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="mkt-home-prompt">
        <ShoppingBag className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-slate-900">Want to see marketplace items near you here?</p>
          <p className="text-xs text-slate-500">Locals sell furniture, electronics and more — ShipitNow drivers deliver.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => savePref('on')}
            className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
            data-testid="mkt-home-yes">
            Yes, show them
          </button>
          <button onClick={() => savePref('off')}
            className="h-9 px-4 rounded-full border border-slate-300 text-slate-600 text-sm font-medium hover:bg-white"
            data-testid="mkt-home-no">
            No thanks
          </button>
        </div>
      </div>
    );
  }

  if (pref === 'off') {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400" data-testid="mkt-home-off-row">
        <ShoppingBag className="h-3.5 w-3.5" /> Marketplace items near you are hidden
        <Switch checked={false} onCheckedChange={() => savePref('on')} data-testid="mkt-home-toggle" className="scale-75" />
      </div>
    );
  }

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="mkt-home-strip">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-4 w-4 text-emerald-600" />
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Marketplace near you</h2>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/marketplace" className="text-xs text-emerald-700 font-medium inline-flex items-center hover:underline" data-testid="mkt-home-see-all">
            See all <ChevronRight className="h-3 w-3" />
          </Link>
          <Switch checked onCheckedChange={() => savePref('off')} data-testid="mkt-home-toggle" className="scale-75" />
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 mt-2">No items near you yet — <Link to="/marketplace/new" className="text-emerald-700 hover:underline">be the first to sell something</Link>.</p>
      ) : (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {items.map((l) => (
            <Link key={l.listing_id} to={`/marketplace/item/${l.listing_id}`}
              className="w-36 shrink-0 rounded-xl border border-slate-200 overflow-hidden hover:border-emerald-300 hover:shadow-sm transition"
              data-testid={`mkt-home-item-${l.listing_id}`}>
              <div className="h-24 bg-slate-100">
                {l.photos?.[0] && <img src={l.photos[0]} alt={l.title} className="w-full h-full object-cover" />}
              </div>
              <div className="p-2">
                <p className="text-xs font-medium text-slate-900 truncate">{l.title}</p>
                <p className="text-xs text-emerald-700 font-semibold">{formatMoney(l.price, l.currency)}</p>
                {l.distance_km != null && (
                  <p className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{l.distance_km} km</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeMarketplaceStrip;
